import { Button, Header, LoadingSpinner, Popup } from '../Generic'
import { observer } from 'mobx-react'
import type { FC } from 'react'
import { useContext, useEffect, useState } from 'react'

import { StoreContext } from '@store/Root.store'

import { PermissionsErrorScreen } from './PermissionsErrorScreen'
import './MediaPermissionsChecker.css'
import { actionReport } from '@methods/actionReport'
import { addSearchParamsAndReload } from '@/methods/forceRedirect'
import { devLog } from '@/methods/devLog'
import { Illustration } from '../Illustration/Illustration'

interface MediaPermissionsCheckerProps {
  onPermissionCheckEnd: (checked: boolean) => void
}

export const MediaPermissionsChecker: FC<MediaPermissionsCheckerProps> = observer((props) => {
  const store = useContext(StoreContext)
  const { isMobile, userAgent } = store.AppState
  const { themeId } = store.InterfaceState
  const { MediaPermissionsChecker: trans } = store.TranslationsState.translations
  const { browser, isMobile: isMobileAgent, system } = userAgent

  const searchParams = new URLSearchParams(window.location.search)
  const isRetryScenario = searchParams.get('isVideoIdRetry') === 'true'

  const [isLoading, setIsLoading] = useState(false)

  const [error, setError] = useState('')
  const [isPopupVisible, setIsPopupVisible] = useState(false)
  const [shouldShowPopup, setShouldShowPopup] = useState(true)
  const [permissionCheckAttempts, setPermissionCheckAttempts] = useState(0)

  const initialCheck = async () => {
    setIsLoading(true)
    try {
      const cameraPerm = await navigator.permissions.query({
        name: 'camera',
        //eslint-disable-next-line
      } as any)
      const microphonePerm = await navigator.permissions.query({
        name: 'microphone',
        //eslint-disable-next-line
      } as any)

      if (cameraPerm.state === 'denied') {
        actionReport({
          type: 'event.onboarding-web.process.MPC_CAMERA_PERMISSIONS_DENIED',
          payload: { browser, system, isMobile: isMobileAgent },
        })
      }

      if (microphonePerm.state === 'denied') {
        actionReport({
          type: 'event.onboarding-web.process.MPC_MICROPHONE_PERMISSIONS_DENIED',
          payload: { browser, system, isMobile: isMobileAgent },
        })
      }

      if (cameraPerm.state === 'granted' && microphonePerm.state === 'granted') {
        props.onPermissionCheckEnd(true)
      }
      //eslint-disable-next-line
    } catch (error) {}
    setIsLoading(false)
  }

  const checkPermissions = async () => {
    setPermissionCheckAttempts((prev) => prev + 1)

    try {
      // firefox and samsung browser don't fully support permissions API
      if (browser !== 'firefox' && browser !== 'samsung') {
        const cameraPerm = await navigator.permissions.query({
          name: 'camera',
          //eslint-disable-next-line
        } as any)

        const microphonePerm = await navigator.permissions.query({
          name: 'microphone',
          //eslint-disable-next-line
        } as any)

        if (cameraPerm.state === 'granted' && microphonePerm.state === 'granted') {
          props.onPermissionCheckEnd(true)
        }

        if (cameraPerm.state === 'denied') {
          actionReport({
            type: 'event.onboarding-web.process.MPC_CAMERA_PERMISSIONS_DENIED',
            payload: { browser, system, isMobile: isMobileAgent },
          })
        }

        if (microphonePerm.state === 'denied') {
          actionReport({
            type: 'event.onboarding-web.process.MPC_MICROPHONE_PERMISSIONS_DENIED',
            payload: { browser, system, isMobile: isMobileAgent },
          })
        }

        if (cameraPerm.state === 'denied' || microphonePerm.state === 'denied') {
          setError('Error: Access denied')
        }

        requestAccess()
      } else {
        requestAccess()
      }
    } catch (error) {
      devLog('Error checking permissions:', error)

      // Log detailed info about the error
      actionReport({
        type: 'event.onboarding-web.process.MPC_PERMISSIONS_CHECK_ERROR',
        payload: {
          error: String(error),
          browser,
          system,
          isMobile: isMobileAgent,
          attempts: permissionCheckAttempts,
        },
      })

      setError('Error checking permissions')
    }
  }

  const requestAccess = async () => {
    setIsPopupVisible(true)

    try {
      // Request access to camera and microphone
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      })

      //end stream
      stream.getTracks().forEach((track) => track.stop())

      props.onPermissionCheckEnd(true)
      // ... Use the media stream ...
    } catch (error) {
      devLog('Access denied:', error)
      setError('Error: Access denied')

      const errMessage = String(error)

      if (errMessage.includes('Permission') || errMessage.includes('denied')) {
        actionReport({
          type: 'event.onboarding-web.process.MPC_MEDIA_ACCESS_SET_TO_DENY',
          payload: {
            error: errMessage,
            browser,
            system,
            isMobile: isMobileAgent,
          },
        })
        return false
      } else {
        // Could be a hardware issue (no camera/mic) rather than permissions
        actionReport({
          type: 'event.onboarding-web.process.MPC_MEDIA_HARDWARE_ERROR',
          payload: {
            error: errMessage,
            browser,
            system,
            isMobile: isMobileAgent,
          },
        })
        return false
      }
    }
  }

  const startPermissionCheck = () => {
    startLoadingToDebouncePopup()
    void checkPermissions()
  }

  useEffect(() => {
    if (error) {
      setIsPopupVisible(false)
    }
  }, [error])

  useEffect(() => {
    actionReport({
      type: 'event.onboarding-web.process.MPC_DEVICE_USER_AGENT',
      payload: {
        userAgent: window.navigator.userAgent,
      },
    })

    if (isRetryScenario) {
      startPermissionCheck()
    } else if (browser !== 'firefox' && browser !== 'samsung') {
      void initialCheck()
    }
  }, [])

  useEffect(() => {
    if (isMobile || isMobileAgent || browser === 'safari') {
      setShouldShowPopup(false)
    }
  }, [browser, isMobileAgent, isMobile])

  const retryPermissionCheck = () => {
    // Get current retry count
    const currentRetries = parseInt(localStorage.getItem('permRetries') || '0')
    const newRetries = currentRetries + 1

    // Store updated retry count
    localStorage.setItem('permRetries', String(newRetries))

    actionReport({
      type: 'event.onboarding-web.process.MPC_RETRIES_NUMBER_INCREASED',
      payload: {
        retriesNumber: newRetries,
        browser,
        system,
        isMobile: isMobileAgent,
      },
    })

    addSearchParamsAndReload({ isVideoIdRetry: 'true' })
  }

  const renderInitScreen = () =>
    isRetryScenario ? (
      <></>
    ) : (
      <>
        <Illustration type="media-permissions-checker-init" />

        <Header className="m-0-0-30">{trans.initHeader}</Header>

        <p className="generic__paragraph-centered m-0-0-30">{trans.initDescription}</p>

        <div className="layout__content-container-bottom-fixed">
          {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
          <Button id="btn-proceed" onClick={startPermissionCheck}>
            <span className="generic__button-text">{trans.proceed}</span>
          </Button>
        </div>
      </>
    )

  const startLoadingToDebouncePopup = () => {
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
    }, 300)
  }

  return (
    <div className="page-container mobile-m-0 vertically-centered without-border">
      {isLoading || (isPopupVisible && !shouldShowPopup) ? (
        <LoadingSpinner />
      ) : (
        <>
          {!error ? renderInitScreen() : <PermissionsErrorScreen onRetry={retryPermissionCheck} />}

          <Popup
            visibility={isPopupVisible && shouldShowPopup}
            handleVisibility={setIsPopupVisible}
            className="media-permissions__popup"
          >
            <Illustration type="media-permissions-checker-arrow" />
            <Header className="m-0-0-30">{trans.popupHeader}</Header>

            <p className="generic__paragraph-centered">{trans.popupDescription}</p>
          </Popup>
        </>
      )}
    </div>
  )
})
